import { API_BASE_URL } from "../Common/Common";

import { sortByMatch } from "../Common/imageMatch";

export default class WikiMedia {
  constructor() {
    this.media = [];
  }

  //https://commons.wikimedia.org/w/api.php?action=query&generator=search&gsrnamespace=6&gsrsearch=%22riva+ligure%22&gsrlimit=50&gsroffset=20&prop=imageinfo&iiprop=url|canonicaltitle&iilimit=1&iiurlwidth=800

  static searchImages = (title, callback, place_id, opts) => {
    const gsrsearch = title.replace(/ /g, "%20");
    //var url = `https://commons.wikimedia.org/w/api.php?action=query&generator=search&gsrnamespace=6&gsrsearch="${gsrsearch}&gsrlimit=50&gsroffset=20&prop=imageinfo&iiprop=url|canonicaltitle&iilimit=1&iiurlwidth=800`;
    // opts.limit: more results (the proxy allows up to 50; default 20).
    const limit = (opts && opts.limit) || (opts && opts.unrestrictedOnly ? 50 : null);
    const url = `${API_BASE_URL}/wikimedia_get_images.php?gsrsearch=${gsrsearch}${limit ? `&limit=${limit}` : ""}`;
    console.log("WikiMedia", url);
    // Plain fetch() with no timeout meant a single hung response (network
    // stall, a slow/wedged PHP proxy) left this place's callback never
    // firing at all — not an error, just silence — which stalled TripAI's
    // whole photo-fetch phase indefinitely on that one place (real report:
    // stuck at 3/31 with no further progress). Matches the AbortController
    // pattern already used elsewhere (GooglePlaces.js's searchPlacesNewAPI).
    // Found/prioritized 2026-09-05.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    fetch(url, { signal: controller.signal })
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        clearTimeout(timeoutId);
        //console.log("response", JSON.stringify(response));
        var images = WikiMedia.processImages(response);
        WikiMedia._finish(images, place_id, callback, opts);
      })
      .catch(function (error) {
        clearTimeout(timeoutId);
        console.log("Error in Wikimedia search", error);
        callback([], place_id);
      });
  };


  static searchImagesNear = (lat, lon, callback, place_id, radiusM = 1500, opts) => {
    const url = `${API_BASE_URL}/wikimedia_get_images.php?ggscoord=${encodeURIComponent(`${lat}|${lon}`)}&ggsradius=${radiusM}${opts && opts.unrestrictedOnly ? "&limit=50" : ""}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    fetch(url, { signal: controller.signal })
      .then((response) => response.json())
      .then((response) => {
        clearTimeout(timeoutId);
        WikiMedia._finish(WikiMedia.processImages(response), place_id, callback, opts);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        console.log("Error in Wikimedia nearby search", error);
        callback([], place_id);
      });
  };

  // Commons "geosearch": files that have coordinates near a point. Text search
  // alone returns whatever matches the words anywhere in the world, which is
  // weak for generic names ("Cafe"); this returns local photos instead.
  static _hasCoords = (lat, lon) => {
    const la = parseFloat(lat);
    const lo = parseFloat(lon);
    return !isNaN(la) && !isNaN(lo) && !(la === 0 && lo === 0);
  };

  // Text AND location in ONE query: Commons' search accepts
  // "nearcoord:<radius>km,<lat>,<lon>" inside the search words, so results must
  // both match the words and have coordinates within the radius ("cathedral"
  // near Cannes gives Antibes' cathedral, not Canterbury's). If that finds few
  // photos (many files have no coordinates), plain text matches are added
  // after them, biased toward the area by appending opts.city / opts.country.
  // opts: { nearbyOnly, radiusKm (default 10), city, country, unrestrictedOnly }.
  // No usable coordinates: plain text search (with city/country appended).
  static searchImagesForPlace = (title, lat, lon, callback, place_id, opts) => {
    const area = [title, opts && opts.city, opts && opts.country].filter(Boolean).join(" ");
    if (!WikiMedia._hasCoords(lat, lon)) {
      WikiMedia.searchImages(area, callback, place_id, opts);
      return;
    }
    // Text AND location, in rings: the same words with nearcoord radii of 1, 3
    // and 10 km, searched in parallel. A file is tagged with the smallest ring
    // that found it (item.distanceWithinKm), so closer photos can rank ahead of
    // farther ones and the details card can say how near it is. (Commons' own
    // coordinates field returns almost none of these files' locations, so exact
    // distances aren't available.)
    const maxKm = (opts && opts.radiusKm) || 10;
    // Large places (radius over 10 km — see Common/placeSize.js): a wider area,
    // and distance doesn't affect the ranking (their photos are taken from far away).
    const large = maxKm > 10;
    // One search over the whole radius for large places, so the order is Commons'
    // own relevance, not "closest ring first" (which would put the summit area
    // ahead of the classic views from the lakes).
    const rings = large ? [maxKm] : [...new Set([1, 3, maxKm].filter((r) => r <= maxKm))].sort((a, b) => a - b);
    const byRing = {};
    let pending = rings.length;
    const rank = (list) => sortByMatch(list, (opts && opts.matchName) || title, { lat, lon, ignoreDistance: large });
    const done = () => {
      const seen = new Set();
      const near = [];
      rings.forEach((r) => {
        (byRing[r] || []).forEach((img) => {
          if (seen.has(img.name)) return;
          seen.add(img.name);
          img.distanceWithinKm = r;
          near.push(img);
        });
      });
      if ((opts && opts.nearbyOnly) || near.length >= 8) {
        callback(rank(near).slice(0, 40), place_id);
        return;
      }
      WikiMedia.searchImages(
        area,
        (text) => {
          // Rank each group on its own and keep the nearby group first — ranking
          // the merged list would let a far-away file with a better name match
          // outrank a nearby one.
          const textOnly = (text || []).filter((i) => !seen.has(i.name));
          callback([...rank(near), ...rank(textOnly)].slice(0, 40), place_id);
        },
        place_id,
        opts
      );
    };
    rings.forEach((r) => {
      const nearQuery = `${title} nearcoord:${r}km,${parseFloat(lat)},${parseFloat(lon)}`;
      WikiMedia.searchImages(
        nearQuery,
        (imgs) => {
          byRing[r] = imgs || [];
          pending -= 1;
          if (pending === 0) done();
        },
        place_id,
        opts
      );
    });
  };

  // License data comes from Commons' extmetadata (LicenseShortName). Every
  // Commons file is free to reuse, but most require crediting the author (CC
  // BY, CC BY-SA). "Unrestricted" = public domain / CC0 / "no restrictions" —
  // nothing to credit and no share-alike, so safe to use automatically.
  static isUnrestricted = (license) => {
    const l = String(license || "").toLowerCase();
    if (!l) return false; // unknown license: don't assume
    if (/\b(by|sa|nc|nd)\b/.test(l)) return false;
    if (/attribution/.test(l) && !/not required/.test(l)) return false;
    return /(public domain|^pd\b|^pd-|cc0|cc-zero|no restrictions|attribution not required)/.test(l);
  };

  // Commons metadata fields are HTML fragments (links, spans); reduce to text.
  static _plain = (html) =>
    String(html || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&quot;/g, '"')
      .replace(/&#0?39;/g, "'")
      .replace(/\s+/g, " ")
      .trim();

  // opts.unrestrictedOnly: ask for more results (50) and keep only the ones
  // with no usage conditions.
  static _finish = (images, place_id, callback, opts) => {
    const out = opts && opts.unrestrictedOnly ? images.filter((i) => WikiMedia.isUnrestricted(i.license)) : images;
    callback(out, place_id);
  };

  // Commons' text search has no content-type filter — a query can just as
  // easily match a scanned book/PDF page (its OCR'd text mentions the
  // place) as an actual photo. Wikimedia renders those as a page-image JPG
  // thumbnail too, so they're indistinguishable by file extension alone;
  // the giveaway is the ORIGINAL scanned file's own extension still present
  // earlier in the filename (".pdf"/".djvu"), or "page123-" page-number
  // prefixes book/document scans use. Added 2026-08-29 after a real example
  // (a 19th-century travel book's page scan) got picked as a place photo —
  // see memory: project_cost_revenue_viability.
  static _isLikelyScannedDocument = (canonicaltitle) => {
    const lower = canonicaltitle.toLowerCase();
    return /\.(pdf|djvu)\b/.test(lower) || /(^|\/)page\d+-/.test(lower) || /\(ia_|%28ia_/.test(lower);
  };

  static processImages = (response) => {
    let images = [];
    if (typeof response.query === "undefined") return [];
    if (typeof response.query.pages === "undefined") return [];
    // pages is an object keyed by page id, so iterating it lists files by id
    // (roughly upload order) and ignores Commons' own relevance rank, which is
    // each page's `index` — order by that instead.
    const ordered = Object.keys(response.query.pages).sort(
      (a, b) => (response.query.pages[a].index || 1e9) - (response.query.pages[b].index || 1e9)
    );
    for (const key of ordered) {
      {
        const imageinfo = response.query.pages[key].imageinfo;
        if (!imageinfo || imageinfo.length === 0) continue;
        const photo = imageinfo[0];
        if (WikiMedia._isLikelyScannedDocument(photo.canonicaltitle)) continue;
        images.push({
          name: photo.canonicaltitle.replace("File:", ""),
          // App-wide convention (confirmed via Place.js/PlacesHeader.js
          // comments): `url` -> Place.Photo is the SMALL/thumbnail field,
          // `url_m` -> Place.Photo_m is the LARGE/full-res one displayed
          // prominently in itineraries/detail views. Was inverted here
          // 2026-08-31 before this correction — thumburl (800px-capped) is
          // the actual thumbnail; url is the genuine full-resolution original.
          license: (photo.extmetadata && photo.extmetadata.LicenseShortName && photo.extmetadata.LicenseShortName.value) || "",
          unrestricted: WikiMedia.isUnrestricted(photo.extmetadata && photo.extmetadata.LicenseShortName && photo.extmetadata.LicenseShortName.value),
          // Extra details, shown only on demand in the image chooser.
          author: WikiMedia._plain(photo.extmetadata && photo.extmetadata.Artist && photo.extmetadata.Artist.value).replace(/^Creator:\s*/i, ""),
          description: WikiMedia._plain(photo.extmetadata && photo.extmetadata.ImageDescription && photo.extmetadata.ImageDescription.value).slice(0, 240),
          width: photo.width || 0,
          height: photo.height || 0,
          pageUrl: photo.descriptionurl || "",
          url: photo.thumburl,
          url_m: photo.url || photo.thumburl,
          // The original is already url_m; the credit travels with the pick
          // (Common/photoCredit.js). 2026-10-17.
          url_print: photo.url || null,
          credit: {
            author: WikiMedia._plain(photo.extmetadata && photo.extmetadata.Artist && photo.extmetadata.Artist.value).replace(/^Creator:\s*/i, ""),
            license: (photo.extmetadata && photo.extmetadata.LicenseShortName && photo.extmetadata.LicenseShortName.value) || "",
            source: "Wikimedia Commons",
            pageUrl: photo.descriptionurl || "",
          },
          folder: "",
          // Own id per file so multi-select can tell them apart.
          id: `wm:${photo.canonicaltitle}`,
          date: "",
          type: 0,
        });
      }
    }
    return images;
  };
}
